/**
 * Geocoding wrapper — Tayro's own server, Apple behind it. No third-party
 * geocoder is ever called from the device.
 *
 * ## 2026-08-16 founder ruling §8.3 — NOMINATIM IS GONE
 *
 * This module used to call the OSM Foundation's Nominatim geocoder **directly
 * from the athlete's device**, which handed OSMF the raw device coordinates
 * *and* the device IP on every "Detect My Location" press — a minor's
 * coordinates included (REL-20 §4, §8.3). It is removed, not gated:
 *
 *   · `reverseGeocode` → `GET /api/reverse-geocode` (Apple Maps Server API,
 *     server-side, server-cached)
 *   · `geocodeCityOnSave` → `GET /api/geocode` (same, forward)
 *
 * ⛔ **No fetch in this file may name a host.** Every request goes through
 * `apiUrl('/api/…')`, which resolves to our own origin (or the baked
 * `VITE_API_BASE` inside the Capacitor shell). A drift-guard test asserts the
 * source contains no third-party geocoder ADDRESS — prose like this paragraph
 * is fine and is how the next reader learns why — and the export surface is
 * pinned.
 *
 * ## §8.2 — coordinates are CITY-LEVEL, rounded at capture
 *
 * `roundToCityLevel` is 2 decimal places ≈ 1.1 km. Every coordinate this
 * module sends or returns has already been through it, so even our own server
 * — and Apple behind it — never receives a precise fix from this path. The
 * write sites round at capture too (`LocationModal.tsx`), and
 * `settingsPersist`'s `_latitude`/`_longitude` specs round again at validate
 * time as a backstop. Weather and the vitamin-D band are the only consumers
 * and both are city-scale questions.
 *
 * ## The two entry points are still USER-TRIGGERED, ONE CALL EACH
 *
 *   1. `reverseGeocode(lat, lon)` — once per "Detect My Location" press
 *   2. `geocodeCityOnSave(query)` — once per Save / "Check this location" tap
 *
 * Autocomplete-as-you-type is still NOT offered. That began as an OSM policy
 * constraint; it stays because a keystroke-rate geocoder is a per-keystroke
 * disclosure of what the athlete is typing, and because the UI copy the
 * athlete has been shown ("Nothing is searched as you type") is a promise.
 * The corrected path if it is ever wanted is a debounced *server* endpoint
 * with its own quota, never a device-side one.
 *
 * The wrapper also keeps:
 *   - an LRU cache (50 entries) keyed by city-level coords / normalized query
 *   - a 10 s per-call timeout
 *   - AbortSignal handling on every call
 */

const REQUEST_TIMEOUT_MS = 10_000;
const CACHE_MAX_ENTRIES = 50;
/** §8.2 — 2 dp ≈ 1.1 km. The ONE definition; do not inline `100` anywhere. */
const CITY_COORD_DECIMALS = 2;

// ─────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────

export interface ReverseGeocodeResult {
  city: string;
  region: string;
  /** Formatted display like "Boulder, CO" or coords fallback. */
  displayName: string;
  /** Whether this response came from the in-memory cache. */
  cached: boolean;
}

export interface ForwardGeocodeMatch {
  /** Best-match display label ("Boulder, CO"). */
  label: string;
  lat: number;
  lon: number;
}

/** Three Vitamin D risk bands per vanilla `settings-page.js:503-504`:
 *  `|lat| ≤ 35 → low · ≤ 45 → elevated · > 45 → high`. */
export type VitaminDRiskBand = 'low' | 'elevated' | 'high';

// ─────────────────────────────────────────────────────────────
// Cache (module-scope singleton)
// ─────────────────────────────────────────────────────────────

import { apiUrl } from './apiBase';

const cache = new Map<string, ReverseGeocodeResult | ForwardGeocodeMatch | null>();

function makeAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

/**
 * Transport-level failure — network down, timeout, non-2xx, malformed body.
 * Categorically different from "there is no such place", and callers must be
 * able to tell them apart: telling an athlete "we couldn't find that place"
 * about a dropped connection is a lie about their input (F5 review finding
 * F-2).
 *
 * Deliberately NOT exported — the export surface is pinned by the drift guard
 * in `locationGeocode.test.ts`.
 * ⚠️ TIMEOUTS ARE NOT THIS ERROR (delta-verify N-1): timedFetch's own 10s
 * timer aborts its internal controller, and that rejection escapes as a
 * plain `AbortError`. Callers MUST discriminate on THEIR OWN AbortSignal
 * (`if (myController.signal.aborted) return;`) — never on the error name —
 * and treat every other rejection (this class AND a timeout's AbortError)
 * as "couldn't check right now". Name-matching on this class and swallowing
 * the rest re-creates the F-1 wedged-spinner defect.
 */
class GeocodeTransportError extends Error {
  /** The underlying rejection. Named `reason` rather than `cause` because
   *  this package targets ES2020, where `Error.cause` is not in lib. */
  readonly reason?: unknown;

  constructor(reason?: unknown) {
    super('Geocoding request failed');
    this.name = 'GeocodeTransportError';
    this.reason = reason;
  }
}

function cachePut<T>(key: string, value: T): T {
  // LRU eviction — when full, drop the oldest (first-inserted) entry.
  if (cache.size >= CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, value as ReverseGeocodeResult | ForwardGeocodeMatch | null);
  return value;
}

function cacheGet<T>(key: string): T | undefined {
  if (!cache.has(key)) return undefined;
  // Refresh recency — re-insert so LRU eviction picks the right victim.
  const value = cache.get(key);
  cache.delete(key);
  if (value !== undefined) cache.set(key, value);
  return value as T;
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/**
 * §8.2 — round a coordinate to city level (2 dp ≈ 1.1 km).
 *
 * ⚖️ THE POINT IS *WHEN*, NOT *WHETHER*. Call this at CAPTURE — the moment a
 * precise fix enters the app — so the precise value is never held in state,
 * never rendered, never sent and never stored. Rounding just before the
 * database is strictly weaker: everything upstream of it has already seen the
 * real number.
 *
 * Non-finite input passes through untouched so callers keep their own
 * validation error rather than being handed a silent `NaN`.
 */
export function roundToCityLevel(n: number): number {
  if (!Number.isFinite(n)) return n;
  const scale = Math.pow(10, CITY_COORD_DECIMALS);
  return Math.round(n * scale) / scale;
}

function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

function reverseCacheKey(lat: number, lon: number): string {
  return `r:${lat},${lon}`;
}

function forwardCacheKey(query: string): string {
  return `f:${normalizeQuery(query)}`;
}

function formatCoordsFallback(lat: number, lon: number): string {
  return `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
}

/** Compose `fetch` with a 10s timeout that honors an external AbortSignal. */
async function timedFetch(url: string, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener('abort', onExternalAbort, { once: true });
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onExternalAbort);
  }
}

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────

/**
 * Reverse-geocode lat/lon → city + region (city/state), via our own
 * `/api/reverse-geocode` (Apple Maps Server API server-side).
 *
 * **USER-TRIGGERED ONLY.** Called once per "Detect My Location" click after
 * `navigator.geolocation` resolves.
 *
 * ⚖️ §8.2 — the coordinates are rounded to city level BEFORE the request is
 * built, so the precise fix does not leave the device even to our own server.
 * The cache is keyed on the rounded pair for the same reason.
 *
 * Throws `DOMException('Aborted', 'AbortError')` when the supplied
 * AbortSignal fires.
 *
 * On network failure or malformed response, returns a coords-only fallback
 * (`displayName = "30.27, -97.74"`) — caller still saves the lat/lon so the
 * VitD risk derivation works.
 */
export async function reverseGeocode(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<ReverseGeocodeResult> {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new Error('Invalid coordinates');
  }
  const cityLat = roundToCityLevel(lat);
  const cityLon = roundToCityLevel(lon);

  const cacheKey = reverseCacheKey(cityLat, cityLon);
  const cached = cacheGet<ReverseGeocodeResult>(cacheKey);
  if (cached) return { ...cached, cached: true };

  if (signal?.aborted) throw makeAbortError();

  const url = apiUrl(
    `/api/reverse-geocode?lat=${encodeURIComponent(cityLat)}&lng=${encodeURIComponent(cityLon)}`,
  );
  let raw: { city?: string; region?: string } | null = null;
  try {
    const res = await timedFetch(url, signal);
    if (res.ok) {
      const json = (await res.json()) as { place?: { city?: string; region?: string } | null };
      raw = json?.place ?? null;
    }
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') throw err;
    // Network / parse failure → coords-only fallback below.
  }

  const city = typeof raw?.city === 'string' ? raw.city : '';
  const region = typeof raw?.region === 'string' ? raw.region : '';
  const displayName =
    city && region
      ? `${city}, ${region}`
      : city || region || formatCoordsFallback(cityLat, cityLon);

  const result: ReverseGeocodeResult = {
    city,
    region,
    displayName,
    cached: false,
  };
  cachePut(cacheKey, result);
  return result;
}

/**
 * Forward-geocode a single query → at most ONE match (the best one), via our
 * own `/api/geocode` (Apple Maps Server API server-side).
 *
 * **USER-TRIGGERED ONLY.** Called once per Location-modal Save click when the
 * athlete typed a city manually, and once per "Check this location" tap in
 * onboarding. Caller MUST enforce ≥ 3-char minimum before invoking. Cached by
 * normalized query string.
 *
 * ⚖️ This path sends a CITY NAME the athlete typed — never coordinates. The
 * coordinates come back, and the caller rounds them before storing.
 *
 * Returns `null` ONLY when the geocoder genuinely has no such place — caller
 * saves the city text only (VitD chip stays omitted). That null IS cached.
 *
 * Throws `DOMException('Aborted', 'AbortError')` on signal fire.
 *
 * Throws a `GeocodeTransportError` (not exported) on network failure,
 * non-2xx or malformed body — NOT on timeout, which surfaces as a plain
 * `AbortError` (see the class docblock; discriminate on your own signal).
 * Transport failures are NOT cached, so a retry after the network recovers
 * issues a real request. Callers that cannot distinguish may
 * `.catch(() => null)` for the old collapse-to-null behavior; callers with UI
 * should say "couldn't check right now" rather than "we couldn't find that
 * place".
 *
 * **NOTE:** returns a single best match, not an array.
 */
export async function geocodeCityOnSave(
  query: string,
  signal?: AbortSignal,
): Promise<ForwardGeocodeMatch | null> {
  const normalized = normalizeQuery(query);
  if (normalized.length < 3) return null;

  const cacheKey = forwardCacheKey(query);
  if (cache.has(cacheKey)) {
    return cacheGet<ForwardGeocodeMatch | null>(cacheKey) ?? null;
  }

  let match: ForwardGeocodeMatch | null = null;
  try {
    const res = await timedFetch(
      apiUrl(`/api/geocode?q=${encodeURIComponent(query.trim())}`),
      signal,
    );
    if (!res.ok) throw new GeocodeTransportError(`HTTP ${res.status}`);
    const json = (await res.json()) as { match?: ForwardGeocodeMatch | null };
    match = json?.match && Number.isFinite(json.match.lat) ? json.match : null;
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') throw err;
    // Transport failure. Deliberately NOT cached: a network blip must not
    // poison the session cache so that every retry replays the failure with
    // zero requests (F5 review finding F-2). Surfaced as a distinct error so
    // the caller can say "couldn't check right now" rather than falsely
    // asserting the place does not exist.
    throw err instanceof GeocodeTransportError ? err : new GeocodeTransportError(err);
  }

  cachePut(cacheKey, match);
  return match;
}

/**
 * Derive Vitamin D risk band from latitude.
 * `|lat| ≤ 35 → 'low' · 35 < |lat| ≤ 45 → 'elevated' · |lat| > 45 → 'high'`.
 * Vanilla parity per `settings-page.js:503-504`.
 *
 * ⚠️ §8.2 note, stated correctly — an earlier draft of this comment claimed
 * rounding "cannot move a coordinate across a band edge", and its own test
 * disproved it. City-level rounding CAN flip the band, for a latitude within
 * 0.005° (≈ 550 m) of the 35°/45° edge, by exactly one step. That is not a
 * defect to guard against; it is what "we keep your location to the nearest
 * kilometre" means, applied honestly. What matters is that callers derive the
 * band from the coordinate they STORE, never from the precise fix — otherwise
 * the profile carries a band its own latitude does not imply.
 */
export function vitaminDRiskFromLatitude(lat: number): VitaminDRiskBand {
  if (!Number.isFinite(lat)) return 'low';
  const abs = Math.abs(lat);
  if (abs > 45) return 'high';
  if (abs > 35) return 'elevated';
  return 'low';
}

/**
 * Test-only — clears the LRU cache so individual tests are isolated. NOT
 * exposed to runtime callers (the leading `__` is the convention for
 * "test bridge").
 */
export function __resetGeocodeStateForTests(): void {
  cache.clear();
}
